/**
 * @fileoverview Section for Sync Settings on the Specify Listings Configuration page,
 * with the newly expanded rows and configurations guarded by
 * 'Listings Launch: Select Entities and Listings Configuration Setup'.
 *
 * @author Michael Zhang (jinzhang@yext.com)
 */

import {ApplePublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/appleconfiguration';
import {FacebookPublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/facebookconfiguration';
import {GooglePublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/gbpconfiguration';
import {YelpEnterprisePublisherId, YelpKnowledgePublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/yelpconfiguration';
import {DeliveryConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/data/deliveryconfiguration';
import {BingPublisherId, EnterpriseConfiguration, ExtendedNetworkPublisherId, LlmPublisherId} from
  '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/enterpriseconfiguration';
import {Table} from '/ui/components/table/table';
import {Toggle} from '/ui/components/toggle/toggle';
import {Tooltip} from '/ui/components/tooltip/tooltip';

import * as styles from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/specifylistingconfiguration.module.scss';
import AppleLogoSvg from '/src/com/yext/listingslaunchstorm/public/icons/apple-logo-small.svg';
import BingLogoSvg from '/src/com/yext/listingslaunchstorm/public/icons/bing-logo-small.svg';
import FacebookLogoSvg from '/src/com/yext/listingslaunchstorm/public/icons/facebook-logo-small.svg';
import GoogleBusinessProfileLogoSvg from '/src/com/yext/listingslaunchstorm/public/icons/google-business-profile-logo-small.svg';
import YelpLogoSvg from '/src/com/yext/listingslaunchstorm/public/icons/yelp-logo-small.svg';

/**
 * @param {{
 *  enterpriseConfiguration: EnterpriseConfiguration,
 *  syncSettings: !Map<number, DeliveryConfiguration>,
 *  setSyncSettings: function(!Map<number, DeliveryConfiguration>): void,
 *  publisherIds: Array<number>,
 * }} props
 * @return {React.ReactNode}
 */
export function ExpandedSyncSettings({enterpriseConfiguration, syncSettings, setSyncSettings, publisherIds}) {
  const COLUMN_IDS = {
    PUBLISHER: 'publisher',
    OPT_IN: 'optIn',
    UPDATE: 'update',
    CREATION: 'creation',
  };

  const settingsColumns = [
    {
      id: COLUMN_IDS.PUBLISHER,
      label: yext.msg('Publisher'),
      width: '240px',
    },
    {
      id: COLUMN_IDS.OPT_IN,
      label: <TooltipContainer
        unclearText={yext.msg('Automatically Opt In')}
        tooltipText={yext.msg('When enabled, all entities subscribed to the publisher will have their listings automatically opted in for that publisher.')}
      />,
      width: '240px',
    },
    {
      id: COLUMN_IDS.UPDATE,
      label: <TooltipContainer
        unclearText={yext.msg('Update Listings')}
        tooltipText={yext.msg('When enabled, any updates to listings will be applied to the publisher.')}
      />,
      width: '240px',
    },
    {
      id: COLUMN_IDS.CREATION,
      label: <TooltipContainer
        unclearText={yext.msg('Create Listings')}
        tooltipText={yext.msg('When enabled, any missing listings will be created on the publisher\'s site.')}
      />,
      width: '240px',
    },
  ];

  const googleConfig = syncSettings.get(GooglePublisherId);
  const fbConfig = syncSettings.get(FacebookPublisherId);
  const appleConfig = syncSettings.get(ApplePublisherId);
  const bingConfig = syncSettings.get(BingPublisherId);
  const yelpEnterpriseConfig = syncSettings.get(YelpEnterprisePublisherId);
  const llmConfig = syncSettings.get(LlmPublisherId);
  const extendNetworkConfig = syncSettings.get(ExtendedNetworkPublisherId);

  const googleAutomaticallyOptIn = !!googleConfig?.automaticallyOptIn && !enterpriseConfiguration?.gbpConfiguration?.skipSetup;
  const facebookAutomaticallyOptIn = !!fbConfig?.automaticallyOptIn && !enterpriseConfiguration?.facebookConfiguration?.skipSetup;
  const appleAutomaticallyOptIn = !!appleConfig?.automaticallyOptIn && !enterpriseConfiguration?.appleConfiguration?.skipSetup;
  const yelpAutomaticallyOptIn = !!yelpEnterpriseConfig?.automaticallyOptIn && !enterpriseConfiguration?.yelpConfiguration?.skipSetup;

  const settingsDataRowsMap = {
    [GooglePublisherId]: {
      id: 'google-selector',
      [COLUMN_IDS.PUBLISHER]: (
        <>
          <GoogleBusinessProfileLogoSvg className={styles.icon} />
          {yext.msg('Google Business Profile')}
        </>
      ),
      [COLUMN_IDS.OPT_IN]: <Toggle
        id="opt-in-google"
        value={googleAutomaticallyOptIn}
        checked={googleAutomaticallyOptIn}
        disabled={enterpriseConfiguration?.gbpConfiguration?.skipSetup}
        onChange={e => setSyncSettings(DeliveryConfiguration.withAutomaticallyOptIn(syncSettings, [GooglePublisherId], e.target.checked))}
      />,
      [COLUMN_IDS.UPDATE]: <Toggle
        id="update-google"
        value={!googleConfig?.dontSyncListings}
        checked={!googleConfig?.dontSyncListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontSyncListings(syncSettings, [GooglePublisherId], !e.target.checked))}
      />,
      [COLUMN_IDS.CREATION]: <Toggle
        id="creation-google"
        value={!googleConfig?.dontCreateNewListings}
        checked={!googleConfig?.dontCreateNewListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontCreateNewListings(syncSettings, [GooglePublisherId], !e.target.checked))}
      />,
    },
    [FacebookPublisherId]: {
      id: 'facebook-selector',
      [COLUMN_IDS.PUBLISHER]: (
        <>
          <FacebookLogoSvg className={styles.icon} />
          {yext.msg('Facebook')}
        </>
      ),
      [COLUMN_IDS.OPT_IN]: <Toggle
        id="opt-in-facebook"
        value={facebookAutomaticallyOptIn}
        checked={facebookAutomaticallyOptIn}
        disabled={enterpriseConfiguration?.facebookConfiguration?.skipSetup}
        onChange={e => setSyncSettings(DeliveryConfiguration.withAutomaticallyOptIn(syncSettings, [FacebookPublisherId], e.target.checked))}
      />,
      [COLUMN_IDS.UPDATE]: <Toggle
        id="update-facebook"
        value={!fbConfig?.dontSyncListings}
        checked={!fbConfig?.dontSyncListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontSyncListings(syncSettings, [FacebookPublisherId], !e.target.checked))}
      />,
      [COLUMN_IDS.CREATION]: <Toggle
        id="creation-facebook"
        value={!fbConfig?.dontCreateNewListings}
        checked={!fbConfig?.dontCreateNewListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontCreateNewListings(syncSettings, [FacebookPublisherId], !e.target.checked))}
      />,
    },
    [ApplePublisherId]: {
      id: 'apple-selector',
      [COLUMN_IDS.PUBLISHER]: (
        <>
          <AppleLogoSvg className={styles.icon} />
          {yext.msg('Apple')}
        </>
      ),
      [COLUMN_IDS.OPT_IN]: <Toggle
        id="opt-in-apple"
        value={appleAutomaticallyOptIn}
        checked={appleAutomaticallyOptIn}
        disabled={enterpriseConfiguration?.appleConfiguration?.skipSetup}
        onChange={e => setSyncSettings(DeliveryConfiguration.withAutomaticallyOptIn(syncSettings, [ApplePublisherId], e.target.checked))}
      />,
      [COLUMN_IDS.UPDATE]: <Toggle
        id="update-apple"
        value={!appleConfig?.dontSyncListings}
        checked={!appleConfig?.dontSyncListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontSyncListings(syncSettings, [ApplePublisherId], !e.target.checked))}
      />,
      [COLUMN_IDS.CREATION]: <Toggle
        id="creation-apple"
        value={!appleConfig?.dontCreateNewListings}
        checked={!appleConfig?.dontCreateNewListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontCreateNewListings(syncSettings, [ApplePublisherId], !e.target.checked))}
      />,
    },
    [BingPublisherId]: {
      id: 'bing-selector',
      [COLUMN_IDS.PUBLISHER]: (
        <>
          <BingLogoSvg className={styles.icon} />
          {yext.msg('Bing')}
        </>
      ),
      [COLUMN_IDS.OPT_IN]: <Toggle
        id="opt-in-bing"
        value={!!bingConfig?.automaticallyOptIn}
        checked={!!bingConfig?.automaticallyOptIn}
        onChange={e => setSyncSettings(DeliveryConfiguration.withAutomaticallyOptIn(syncSettings, [BingPublisherId], e.target.checked))}
      />,
      [COLUMN_IDS.UPDATE]: <Toggle
        id="update-bing"
        value={!bingConfig?.dontSyncListings}
        checked={!bingConfig?.dontSyncListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontSyncListings(syncSettings, [BingPublisherId], !e.target.checked))}
      />,
      [COLUMN_IDS.CREATION]: <Toggle
        id="creation-bing"
        value={!bingConfig?.dontCreateNewListings}
        checked={!bingConfig?.dontCreateNewListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontCreateNewListings(syncSettings, [BingPublisherId], !e.target.checked))}
      />,
    },
    [YelpEnterprisePublisherId]: {
      id: 'yelp-enterprise-selector',
      [COLUMN_IDS.PUBLISHER]: (
        <>
          <YelpLogoSvg className={styles.icon} />
          {yext.msg('Yelp')}
        </>
      ),
      [COLUMN_IDS.OPT_IN]: <Toggle
        id="opt-in-yelp-enterprise"
        value={yelpAutomaticallyOptIn}
        checked={yelpAutomaticallyOptIn}
        disabled={enterpriseConfiguration?.yelpConfiguration?.skipSetup}
        onChange={e => setSyncSettings(DeliveryConfiguration.withAutomaticallyOptIn(syncSettings, [YelpEnterprisePublisherId, YelpKnowledgePublisherId], e.target.checked))}
      />,
      [COLUMN_IDS.UPDATE]: <Toggle
        id="update-yelp-enterprise"
        value={!yelpEnterpriseConfig?.dontSyncListings}
        checked={!yelpEnterpriseConfig?.dontSyncListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontSyncListings(syncSettings, [YelpEnterprisePublisherId, YelpKnowledgePublisherId], !e.target.checked))}
      />,
      [COLUMN_IDS.CREATION]: <Toggle
        id="creation-yelp-enterprise"
        value={!yelpEnterpriseConfig?.dontCreateNewListings}
        checked={!yelpEnterpriseConfig?.dontCreateNewListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontCreateNewListings(syncSettings, [YelpEnterprisePublisherId, YelpKnowledgePublisherId], !e.target.checked))}
      />,
    },
    [LlmPublisherId]: {
      id: 'llm-selector',
      [COLUMN_IDS.PUBLISHER]: yext.msg('Generative AI Publishers'),
      [COLUMN_IDS.OPT_IN]: <Toggle
        id="opt-in-llm"
        value={!!llmConfig?.automaticallyOptIn}
        checked={!!llmConfig?.automaticallyOptIn}
        onChange={e => setSyncSettings(DeliveryConfiguration.withAutomaticallyOptIn(syncSettings, [LlmPublisherId], e.target.checked))}
      />,
      [COLUMN_IDS.UPDATE]: <Toggle
        id="update-llm"
        value={!llmConfig?.dontSyncListings}
        checked={!llmConfig?.dontSyncListings}
        disabled={true}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontSyncListings(syncSettings, [LlmPublisherId], !e.target.checked))}
      />,
      [COLUMN_IDS.CREATION]: <Toggle
        id="creation-llm"
        value={!llmConfig?.dontCreateNewListings}
        checked={!llmConfig?.dontCreateNewListings}
        disabled={true}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontCreateNewListings(syncSettings, [LlmPublisherId], !e.target.checked))}
      />,
    },
    [ExtendedNetworkPublisherId]: {
      id: 'extended-selector',
      [COLUMN_IDS.PUBLISHER]: yext.msg('Extended Network'),
      [COLUMN_IDS.OPT_IN]: <Toggle
        id="opt-in-extended"
        value={!!extendNetworkConfig?.automaticallyOptIn}
        checked={!!extendNetworkConfig?.automaticallyOptIn}
        onChange={e => setSyncSettings(DeliveryConfiguration.withAutomaticallyOptIn(syncSettings, [ExtendedNetworkPublisherId], e.target.checked))}
      />,
      [COLUMN_IDS.UPDATE]: <Toggle
        id="update-extended"
        value={!extendNetworkConfig?.dontSyncListings}
        checked={!extendNetworkConfig?.dontSyncListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontSyncListings(syncSettings, [ExtendedNetworkPublisherId], !e.target.checked))}
      />,
      [COLUMN_IDS.CREATION]: <Toggle
        id="creation-extended"
        value={!extendNetworkConfig?.dontCreateNewListings}
        checked={!extendNetworkConfig?.dontCreateNewListings}
        disabled={true}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontCreateNewListings(syncSettings, [ExtendedNetworkPublisherId], !e.target.checked))}
      />,
    },
  };

  const settingsDataRows = publisherIds
    .filter(publisherId => !!settingsDataRowsMap[publisherId])
    .map(publisherId => settingsDataRowsMap[publisherId]);

  return (
    <div className="configuration-section">
      <Table
        label={yext.msg('Listings Sync Settings')}
        columns={settingsColumns}
        dataRows={settingsDataRows}
      />
    </div>
  );
}

function TooltipContainer({unclearText, tooltipText}) {
  return (
    <div className={styles.tooltipContainer}><Tooltip unclearText={unclearText} tooltipText={tooltipText} /></div>
  );
}
